import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Checkbox, Col, DatePicker, Row, Select, Statistic, Table, Tag } from 'antd';
import { CalendarOutlined, SearchOutlined } from '@ant-design/icons';
import Chart from 'react-apexcharts';
import moment from 'moment';
import { GetCasas } from '@actions/casasActions';
import { GetEmps } from '@actions/empsActions';
import { GetDashboardOrcamentos, GetOrcamentos } from '@actions/orcamentosActions';
import { SetPageHeader } from '@actions/userActions';
import { useAppDispatch, useAppSelector } from '@config/reduxStore';
import { OrcamentoDashboard } from '@models/Dashboard';
import { naturalSorter } from '@utils/filterandsort';
import { doubleMask, moneyMask } from '@utils/masks';

interface DashboardValorM2Row extends OrcamentoDashboard {
  orcadoM2: number | null;
  realizadoM2: number | null;
  projetadoM2: number | null;
  semArea: boolean;
}

const safeM2 = (value: number, area: number) => (area > 0 ? value / area : null);

const formatM2 = (value: number | null) => (value === null ? 'Sem área' : `${moneyMask.format(value)} /m²`);

const shortLabel = (value: string = '', maxLength: number = 24) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;

const getOrcamentoTipo = (value: string = '') => {
  const upperValue = value.toUpperCase();
  if (upperValue.includes('RESID')) return 'Resid';
  if (upperValue.includes('ALL')) return 'ALL';
  return '';
};

const getCasaEmpreendimentoNome = (casa: any) =>
  typeof casa.empreendimento === 'string' ? casa.empreendimento : casa.empreendimento?.nome || '';

function DashboardValorM2() {
  const dispatch = useAppDispatch();
  const [orcamento, setOrcamento] = useState<string>();
  const [empreendimento, setEmpreendimento] = useState<string>();
  const [somenteComArea, setSomenteComArea] = useState(false);
  const [mesContrato, setMesContrato] = useState(moment());
  const [orcamentoOpen, setOrcamentoOpen] = useState(false);
  const [empreendimentoOpen, setEmpreendimentoOpen] = useState(false);
  const [chartHeight, setChartHeight] = useState(520);
  const { dashboard, loadingDashboard, orcamentos } = useAppSelector((state) => state.orcamentos);
  const { casas } = useAppSelector((state) => state.casas);
  const { emps } = useAppSelector((state) => state.emps);

  useEffect(() => {
    dispatch(GetDashboardOrcamentos(true));
    dispatch(GetOrcamentos(true));
    dispatch(GetEmps());
    dispatch(GetCasas());
  }, [dispatch]);

  useEffect(() => {
    const updateChartHeight = () => {
      setChartHeight(Math.min(Math.max(Math.round(window.innerHeight * 0.56), 440), 560));
    };

    updateChartHeight();
    window.addEventListener('resize', updateChartHeight);

    return () => window.removeEventListener('resize', updateChartHeight);
  }, []);

  useEffect(() => {
    dispatch(
      SetPageHeader({
        title: 'Dashboard Valor por m²',
        backbutton: true,
        extras: [],
        subtitle: 'Orçado x Realizado x Projetado',
        menu: 'orcamentosmain',
      })
    );
  }, [dispatch]);

  const empreendimentosPorOrcamentoId = useMemo(
    () => new Map<number, string>(orcamentos.map((item) => [Number(item.id), item.empreendimento])),
    [orcamentos]
  );

  const empreendimentosPorId = useMemo(
    () => new Map<number, string>(emps.map((emp) => [Number(emp.id), emp.nome])),
    [emps]
  );

  const rows = useMemo<DashboardValorM2Row[]>(() => {
    return dashboard.map((item) => {
      const area = Number(item.area || 0);

      return {
        ...item,
        empreendimento:
          empreendimentosPorOrcamentoId.get(Number(item.id)) ||
          empreendimentosPorId.get(Number(item.empreendimentoID)) ||
          item.empreendimento ||
          '',
        orcadoM2: safeM2(Number(item.orcado || 0), area),
        realizadoM2: safeM2(Number(item.realizado || 0), area),
        projetadoM2: safeM2(Number(item.projetado || 0), area),
        semArea: area <= 0,
      };
    });
  }, [dashboard, empreendimentosPorId, empreendimentosPorOrcamentoId]);

  const empreendimentos = useMemo(() => {
    const rowsDoTipo = orcamento ? rows.filter((row) => getOrcamentoTipo(row.obra) === orcamento) : rows;

    return [...new Set(rowsDoTipo.map((row) => row.empreendimento).filter(Boolean))]
      .map((value) => String(value))
      .sort((a, b) => naturalSorter(a, b));
  }, [orcamento, rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const matchOrcamento = !orcamento || getOrcamentoTipo(row.obra) === orcamento;
      const matchEmpreendimento = !empreendimento || row.empreendimento === empreendimento;
      const matchArea = !somenteComArea || !row.semArea;

      return matchOrcamento && matchEmpreendimento && matchArea;
    });
  }, [empreendimento, orcamento, rows, somenteComArea]);

  const orcamentosOptions = useMemo(
    () => ['ALL', 'Resid'].filter((tipo) => rows.some((row) => getOrcamentoTipo(row.obra) === tipo)),
    [rows]
  );

  const rowsComArea = filteredRows.filter((row) => !row.semArea);
  const totalArea = rowsComArea.reduce((total, row) => total + Number(row.area || 0), 0);
  const totalOrcado = rowsComArea.reduce((total, row) => total + Number(row.orcado || 0), 0);
  const totalProjetado = rowsComArea.reduce((total, row) => total + Number(row.projetado || 0), 0);
  const totalRealizado = rowsComArea.reduce((total, row) => total + Number(row.realizado || 0), 0);
  const orcadoMediaM2 = safeM2(totalOrcado, totalArea);
  const projetadoMediaM2 = safeM2(totalProjetado, totalArea);
  const realizadoMediaM2 = safeM2(totalRealizado, totalArea);
  const empreendimentosFiltrados = useMemo(
    () => new Set(filteredRows.map((row) => row.empreendimento).filter(Boolean)),
    [filteredRows]
  );

  const contratosNoMes = casas.filter((casa) => {
    const dataContrato = moment(casa.dataContrato);
    const casaEmpreendimento = getCasaEmpreendimentoNome(casa);
    const matchMes = dataContrato.isValid() && dataContrato.isSame(mesContrato, 'month');
    const matchOrcamento = !orcamento || empreendimentosFiltrados.has(casaEmpreendimento);
    const matchEmpreendimento = !empreendimento || casaEmpreendimento === empreendimento;

    return matchMes && matchOrcamento && matchEmpreendimento;
  }).length;

  const chartRows = rowsComArea
    .filter((row) => row.orcadoM2 !== null || row.realizadoM2 !== null || row.projetadoM2 !== null)
    .sort((a, b) => Number(b.projetadoM2 || 0) - Number(a.projetadoM2 || 0))
    .slice(0, 10);

  const chartOptions = {
    chart: { toolbar: { show: false } },
    dataLabels: { enabled: false },
    grid: { padding: { top: 44, left: 32, right: 32, bottom: 18 } },
    xaxis: {
      categories: chartRows.map((row) => shortLabel(row.obra)),
      labels: {
        rotate: -35,
        rotateAlways: true,
        hideOverlappingLabels: true,
        trim: true,
        minHeight: 80,
        style: { fontSize: '11px' },
      },
    },
    yaxis: {
      labels: {
        formatter: (value: number) => moneyMask.format(value),
      },
    },
    tooltip: {
      custom: ({ series, dataPointIndex }: { series: number[][]; dataPointIndex: number }) => {
        const row = chartRows[dataPointIndex];
        return `<div style="max-width:320px;padding:8px 10px;white-space:normal;">
          <strong>${row.obra}</strong>
          <div>Orçado: ${moneyMask.format(series[0][dataPointIndex])} /m²</div>
          <div>Realizado: ${moneyMask.format(series[1][dataPointIndex])} /m²</div>
          <div>Projetado: ${moneyMask.format(series[2][dataPointIndex])} /m²</div>
        </div>`;
      },
      y: {
        formatter: (value: number) => `${moneyMask.format(value)} /m²`,
      },
    },
  };

  const chartSeries = [
    { name: 'Orçado /m²', data: chartRows.map((row) => Number(row.orcadoM2 || 0)) },
    { name: 'Realizado /m²', data: chartRows.map((row) => Number(row.realizadoM2 || 0)) },
    { name: 'Projetado /m²', data: chartRows.map((row) => Number(row.projetadoM2 || 0)) },
  ];

  const filterSelectStyle = {
    width: '100%',
    border: '2px solid #111',
    borderRadius: 2,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.18)',
  };

  const filterButtonStyle = {
    border: '2px solid #111',
    color: '#111',
    fontWeight: 500,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.18)',
  };
  const mesContratoAtivo = !!mesContrato;

  const renderFilterSuffixIcon = (active: boolean, onSearchClick: () => void) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 18 }}>
      <SearchOutlined
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onSearchClick();
        }}
        style={{ color: active ? '#1890ff' : '#111', cursor: 'pointer', fontSize: 18 }}
      />
    </span>
  );

  return (
    <div>
      <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
        <Col xs={24} md={12} lg={6}>
          <Card size="small">
            <Statistic title="Orçado" value={orcadoMediaM2 || 0} prefix="R$" precision={2} suffix="/m²" />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card size="small">
            <Statistic title="Projetado" value={projetadoMediaM2 || 0} prefix="R$" precision={2} suffix="/m²" />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card size="small">
            <Statistic title="Realizado" value={realizadoMediaM2 || 0} prefix="R$" precision={2} suffix="/m²" />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card size="small">
            <Statistic title="Contratos no mês" value={contratosNoMes} suffix="casas" />
          </Card>
        </Col>
      </Row>

      <Row gutter={[8, 8]} style={{ marginBottom: 8 }}>
        <Col xs={24} lg={16}>
          <Card size="small" title="Orçado x realizado x projetado por m²">
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
              <div style={{ width: '100%', maxWidth: 1680 }}>
                <Chart options={chartOptions} series={chartSeries} type="bar" height={chartHeight} />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
            <Card size="small" title="Resumo da base">
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Statistic title="Orçamentos" value={filteredRows.length} />
                </Col>
                <Col span={12}>
                  <Statistic title="Área considerada" value={totalArea} suffix="m²" precision={2} />
                </Col>
                <Col span={12}>
                  <Statistic title="Sem área" value={filteredRows.filter((row) => row.semArea).length} />
                </Col>
              </Row>
            </Card>
            <Card size="small" title="Filtros" style={{ flex: 1 }}>
              <div style={{ width: 'min(100%, 360px)', margin: '0 auto', paddingTop: 32 }}>
                <Row gutter={[8, 8]} justify="center">
                  <Col span={24}>
                    <Select
                      showSearch
                      placeholder="Filtrar orçamento"
                      className="dashboard-valor-m2-filter-select"
                      style={filterSelectStyle}
                      suffixIcon={renderFilterSuffixIcon(!!orcamento, () => setOrcamentoOpen((open) => !open))}
                      value={orcamento}
                      open={orcamentoOpen}
                      onDropdownVisibleChange={setOrcamentoOpen}
                      onChange={(value) => {
                        setOrcamento(value);
                        setEmpreendimento(undefined);
                        setOrcamentoOpen(false);
                      }}
                      optionFilterProp="children"
                    >
                      {orcamentosOptions.map((item) => (
                        <Select.Option key={item} value={item}>
                          {item}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={24}>
                    <Select
                      showSearch
                      placeholder="Filtrar empreendimento"
                      className="dashboard-valor-m2-filter-select"
                      style={filterSelectStyle}
                      suffixIcon={renderFilterSuffixIcon(!!empreendimento, () =>
                        setEmpreendimentoOpen((open) => !open)
                      )}
                      value={empreendimento}
                      open={empreendimentoOpen}
                      onDropdownVisibleChange={setEmpreendimentoOpen}
                      onChange={(value) => {
                        setEmpreendimento(value);
                        setEmpreendimentoOpen(false);
                      }}
                      optionFilterProp="children"
                    >
                      {empreendimentos.map((item) => (
                        <Select.Option key={item} value={item}>
                          {item}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                  <Col span={24}>
                    <DatePicker
                      picker="month"
                      allowClear={false}
                      format="MM/YYYY"
                      placeholder="Filtrar mês do contrato"
                      className="dashboard-valor-m2-filter-date"
                      style={filterSelectStyle}
                      suffixIcon={<CalendarOutlined style={{ color: mesContratoAtivo ? '#1890ff' : '#111', fontSize: 18 }} />}
                      value={mesContrato}
                      onChange={(value) => {
                        if (value) setMesContrato(value);
                      }}
                    />
                  </Col>
                  <Col span={24}>
                    <Checkbox
                      className="dashboard-valor-m2-filter-checkbox"
                      checked={somenteComArea}
                      onChange={(event) => setSomenteComArea(event.target.checked)}
                    >
                      Somente itens com área
                    </Checkbox>
                  </Col>
                  <Col span={24}>
                    <Button
                      block
                      style={filterButtonStyle}
                      onClick={() => {
                        setOrcamento(undefined);
                        setEmpreendimento(undefined);
                        setMesContrato(moment());
                        setSomenteComArea(false);
                        setOrcamentoOpen(false);
                        setEmpreendimentoOpen(false);
                      }}
                    >
                      Limpar filtros
                    </Button>
                  </Col>
                </Row>
              </div>
            </Card>
          </div>
        </Col>
      </Row>

      <Card size="small" title="Conciliação por orçamento">
        <Table
          className="dashboard-valor-m2-table"
          rowKey="id"
          size="small"
          loading={loadingDashboard}
          dataSource={filteredRows}
          pagination={false}
          scroll={{ x: 'max-content', y: 'calc(100vh - 560px)' }}
        >
          <Table.Column
            title="Empreendimento"
            dataIndex="empreendimento"
            width={200}
            render={(value) => value || <Tag color="default">Sem empreendimento</Tag>}
            sorter={(a: DashboardValorM2Row, b: DashboardValorM2Row) =>
              naturalSorter(a.empreendimento || '', b.empreendimento || '')
            }
          />
          <Table.Column
            title="Orçamento"
            dataIndex="obra"
            width={320}
            ellipsis
            sorter={(a: DashboardValorM2Row, b: DashboardValorM2Row) => naturalSorter(a.obra, b.obra)}
          />
          <Table.Column
            title="Área"
            dataIndex="area"
            align="right"
            width={120}
            render={(value, row: DashboardValorM2Row) =>
              row.semArea ? <Tag color="orange">Sem área</Tag> : `${doubleMask.format(value)} m²`
            }
            sorter={(a: DashboardValorM2Row, b: DashboardValorM2Row) => Number(a.area || 0) - Number(b.area || 0)}
          />
          <Table.Column
            title="Orçado /m²"
            dataIndex="orcadoM2"
            align="right"
            width={150}
            render={formatM2}
            sorter={(a: DashboardValorM2Row, b: DashboardValorM2Row) => Number(a.orcadoM2 || 0) - Number(b.orcadoM2 || 0)}
          />
          <Table.Column
            title="Realizado /m²"
            dataIndex="realizadoM2"
            align="right"
            width={160}
            render={formatM2}
            sorter={(a: DashboardValorM2Row, b: DashboardValorM2Row) =>
              Number(a.realizadoM2 || 0) - Number(b.realizadoM2 || 0)
            }
          />
          <Table.Column
            title="Projetado /m²"
            dataIndex="projetadoM2"
            align="right"
            width={160}
            render={formatM2}
            sorter={(a: DashboardValorM2Row, b: DashboardValorM2Row) =>
              Number(a.projetadoM2 || 0) - Number(b.projetadoM2 || 0)
            }
          />
        </Table>
      </Card>
      <style>
        {`
          .dashboard-valor-m2-filter-select .ant-select-selector {
            border: 0 !important;
          }

          .dashboard-valor-m2-filter-date {
            width: 100%;
          }

          .dashboard-valor-m2-filter-date input {
            color: #4a4a4a !important;
            font-weight: 500;
          }

          .dashboard-valor-m2-filter-date .ant-picker-suffix {
            color: #111;
          }

          .dashboard-valor-m2-filter-select .ant-select-selection-placeholder,
          .dashboard-valor-m2-filter-select .ant-select-selection-item {
            color: #4a4a4a !important;
            font-weight: 500;
          }

          .dashboard-valor-m2-filter-select .ant-select-arrow,
          .dashboard-valor-m2-filter-select .ant-select-clear {
            color: #111;
          }

          .dashboard-valor-m2-filter-select .ant-select-arrow {
            pointer-events: auto;
          }

          .dashboard-valor-m2-filter-checkbox {
            color: #111;
            font-weight: 500;
          }

          .dashboard-valor-m2-filter-checkbox .ant-checkbox-inner {
            border: 2px solid #111;
            border-radius: 2px;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
          }

          .dashboard-valor-m2-filter-checkbox .ant-checkbox-checked .ant-checkbox-inner {
            background-color: #111;
            border-color: #111;
          }

          .dashboard-valor-m2-table .ant-table-thead > tr > th,
          .dashboard-valor-m2-table .ant-table-tbody > tr > td {
            border-right: 1px solid rgba(0, 0, 0, 0.2);
          }

          .dashboard-valor-m2-table .ant-table-thead > tr > th:last-child,
          .dashboard-valor-m2-table .ant-table-tbody > tr > td:last-child {
            border-right: 0;
          }
        `}
      </style>
    </div>
  );
}

export default DashboardValorM2;
